import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { parse, format } from 'date-fns';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, DateTime } from '~/components/Form';
import { t } from '~/helpers';
import {
  compareIsAfterSDates,
  compareIsBeforeSDates,
} from '~/helpers/dateTime';
import { storePrecoProduto } from '~/store/modules/precoproduto/actions';

import ConfirmDialog from '../components/ConfirmDialog';

export default function CreateOrUpdate({ reference, data }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.precoproduto);
  const [handleConfirmModal, setHandleConfirmModal] = useState(false);
  const [preco, setPreco] = useState(null);
  const [obs, setObs] = useState(null);
  const dt = new Date();
  const [dataDesejada, setDataDesejada] = useState(dt);
  const [dataAtual, setDataAtual] = useState(dt);

  const initials = { ...find, ...data };

  const handleClose = () => {
    setHandleConfirmModal(false);
  };

  // faz a confirmacao em relacao a data
  async function handleSubmitConfirm() {
    const param = reference.current.getData();
    const datapreco = param._datahoraprecoproduto;
    if (datapreco !== '') {
      if (
        compareIsBeforeSDates(
          format(dataAtual, 'dd/MM/yyyy HH:mm:ss'),
          dataDesejada
        )
      ) {
        setHandleConfirmModal(true);
      } else {
        const param = {
          precoproduto: preco,
          _datahoraprecoproduto: dataDesejada,
          obsprecoproduto: obs,
        };
        handleSubmit(param);
      }
    } else {
      const param = {
        precoproduto: preco,
        _datahoraprecoproduto: '',
        obsprecoproduto: obs,
      };
      handleSubmit(param);
    }
  }

  async function handleSubmitConfirmado() {
    const campos = reference.current.getData();
    const param = {
      precoproduto: campos.precoproduto,
      _datahoraprecoproduto: campos._datahoraprecoproduto,
      obsprecoproduto: campos.obsprecoproduto,
    };
    handleSubmit(param);
  }

  async function handleSubmit(param) {
    setHandleConfirmModal(false);
    try {
      const schema = Yup.object().shape({
        precoproduto: Yup.number()
          .positive(t('s.operacao.descarganf.precomaiorzero'))
          .typeError(t('s.operacao.descarganf.precomaiorzero'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        _datahoraprecoproduto: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            '_datahoraprecoproduto',
            t('s.geral.datanaopodesersuperior'),
            () =>
              !compareIsAfterSDates(
                format(dataAtual, 'dd/MM/yyyy HH:mm:ss'),
                param._datahoraprecoproduto
              )
          ),
        obsprecoproduto: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .trim()
          .max(250, t('s.geral.validacao.tamanhomaximo').replace('%s', 250)),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        datahoraprecoproduto: param._datahoraprecoproduto,
        idcliente: client,
      };

      delete objSend._datahoraprecoproduto;

      await dispatch(storePrecoProduto(objSend, params));
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form
            ref={reference}
            initialData={initials}
            onSubmit={handleSubmitConfirm}
          >
            <Input
              name="nomeproduto"
              label={t('s.configuracao.produto.nomeproduto')}
              variant="outlined"
              fullWidth
              size="small"
              disabled
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Input
              name="precoproduto"
              label={t('s.configuracao.produto.precoproduto')}
              type="number"
              getValue={(value) => setPreco(value)}
            />
            <DateTime
              name="_datahoraprecoproduto"
              label={t('s.operacao.turno.datahora')}
              time
              defaultValue={format(new Date(), 'dd/MM/yyyy HH:mm:ss')}
              getValue={(value) => setDataDesejada(value)}
            />
            <Input
              multiline
              name="obsprecoproduto"
              label={t('s.operacao.limpezatag.observacaohistoricotag')}
              rows={4}
              getValue={(value) => setObs(value)}
            />
          </Form>
          <ConfirmDialog
            open={handleConfirmModal}
            onClose={handleClose}
            selectedValue={dataDesejada}
            handleSubmit={() => handleSubmitConfirmado()}
          />
        </>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
